import cx from 'clsx';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Tooltip } from 'ui/component/Tooltip.tsx';

type Props = {
  className?: string;
  title: string;
  description?: string | null;
  icon?: React.ReactNode;
  value: number | undefined;
  percentage?: number;
  comparisonValue?: string;
  upperThreshold?: number;
  upperThresholdPercentage?: number;
  lowerThreshold?: number;
  lowerThresholdPercentage?: number;
  sampleSize?: number; // needed to understand if KPI makes sense or is not applicable (especially for thresholds)
  targetURL?: string;
  color?: 'grey' | 'blue' | 'green' | 'red';
};

export const KPI: React.FC<Props> = ({
  className,
  title,
  description,
  icon,
  value,
  comparisonValue,
  upperThreshold,
  upperThresholdPercentage,
  lowerThreshold,
  lowerThresholdPercentage,
  sampleSize,
  percentage,
  targetURL,
  color,
}: Props) => {
  const navigate = useNavigate();
  const onClick = targetURL ? () => navigate(targetURL) : () => {};

  /**
   * KPI has 4 possible statuses:
   * - if there's no data on which to compute the KPI, then not applicable/gray
   * - if the threshold is not provided, then neutral/blue
   * - if the threshold is provided and value respects it, then success/green
   * - if the threshold is provided and value doesn't respect it, then failure/red
   */
  const status: 'NA' | 'NEUTRAL' | 'SUCCESS' | 'FAILURE' = React.useMemo(() => {
    if (value === undefined) return 'NA';
    else if (upperThreshold !== undefined || lowerThreshold !== undefined || upperThresholdPercentage !== undefined || lowerThresholdPercentage !== undefined) {
      if (!sampleSize) return 'NA'; // if there's no sample size, we cannot really say if the KPI is applicable or not
      if (upperThreshold && value < upperThreshold) return 'SUCCESS';
      if (upperThresholdPercentage && percentage !== undefined && percentage < upperThresholdPercentage) return 'SUCCESS';
      if (lowerThreshold && value > lowerThreshold) return 'SUCCESS';
      if (lowerThresholdPercentage && percentage !== undefined && percentage > lowerThresholdPercentage) return 'SUCCESS';
      return 'FAILURE';
    } else return 'NEUTRAL';
  }, [value, sampleSize, percentage, upperThreshold, upperThresholdPercentage, lowerThreshold, lowerThresholdPercentage]);

  // Exactly one variant applies, and the default border lives here rather than in the base class
  // list so a state border never has to win a same-layer tie against it. The bare colour class
  // already sets the text colour via the unlayered `:not(button).<colour>` rules in style.css —
  // a `text-<colour>-500` alongside it would be dead weight, overridden by that rule.
  let variant = 'border-blue-light';
  if (color === 'grey' || (color === undefined && status === 'NA')) variant = 'grey border-grey';
  else if (color === 'blue' || (color === undefined && status === 'NEUTRAL')) variant = 'blue border-blue';
  else if (color === 'green' || (color === undefined && status === 'SUCCESS')) variant = 'green border-green';
  else if (color === 'red' || (color === undefined && status === 'FAILURE')) variant = 'red border-red';

  return (
    <div
      className={cx('paper !p-6 flex flex-col justify-between gap-2 border-2 bg-white', targetURL && 'cursor-pointer', variant, className)}
      onClick={targetURL ? onClick : undefined}
    >
      <h3
        className={cx(
          '!text-[22px] mr-auto flex w-full place-content-between items-center gap-2 truncate',
          (status === 'NA' || status === 'NEUTRAL' || color === 'blue') && 'text-blue',
          (status === 'SUCCESS' || color === 'green') && 'text-green',
          (status === 'FAILURE' || color === 'red') && 'text-red',
        )}
      >
        <span>{title}</span>
        {icon}
      </h3>
      <div className="relative flex flex-row items-center gap-2">
        {value === undefined && <Spinner className="left-0! translate-x-0!" centered={true} overlay={'bg-white'} />}
        <Tooltip text={description || title} className="inline-block truncate font-bold text-[3.2rem] leading-none">
          {value ?? 'N/A'}
        </Tooltip>
        {percentage !== undefined && <span className="font-normal text-[32px] leading-none">({percentage}%)</span>}
        {comparisonValue !== undefined && <span className="font-normal text-[32px] leading-none">{comparisonValue}</span>}
      </div>
    </div>
  );
};
